import { combineReducers } from 'redux';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { InstallmentDocumentBasic, InstallmentDocumentDetailResponse } from 'generated/bffFinancialInfo/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  AppAction,
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createLoadingStateReducer,
  createReducer,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { buildRoute } from 'helpers/routes/routeBuilder';
import { getCurrentRoute, ROUTES } from 'helpers/routes/routes';
import { AppState } from 'helpers/store/models/AppState';
import { history } from 'helpers/store/rootReducer';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { BillingReqeustFilter, InstallmentCalendarDetailPayload, InstallmentCalendarListPayload } from 'models/Billing';
import { CustomerInfo } from 'models/context/CustomerInfo';

import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { selectContextCustomerInfo } from 'saga/context/customer/ducks';
import { getKahlActions, getKahlOutsideComponentAction } from 'saga/kahl/ducks';
import { selectBillingCaRefNo } from 'saga/billing/ducks';

/* STATE */
export interface InstallmentState {
  installmentList: InstallmentDocumentBasic[];
  loadingStatus: LoadingStatus;
  installmentDetail: InstallmentDocumentDetailResponse;
}

/* ACTION TYPES */
export enum InstallmentActionTypes {
  GetInstallmentList = '@@Billing/GET_INSTALLMENT_LIST',
  ResetInstallmentList = '@@Billing/RESET_INSTALLMENT_LIST',
  SetLoading = '@@Billing/SET_INSTALLMENT_LOADING',
  GetInstallmentCalendarDetail = '@@Billing/GET_INSTALLMENT_CALENDAR_DETAIL',
  ResetInstallmentCalendarDetail = '@@Billing/RESET_CALENDAR_DETAIL',
}

/* ACTIONS */
export const getInstallmentListAction = createApiActionCreators(InstallmentActionTypes.GetInstallmentList);
export const setInstallmentLoadingAction = createActionCreator(InstallmentActionTypes.SetLoading);
export const resetInstallmentListAction = createActionCreator(InstallmentActionTypes.ResetInstallmentList);
export const getInstallmentCalendarDetailAction = createApiActionCreators(
  InstallmentActionTypes.GetInstallmentCalendarDetail
);
export const resetInstallmentCalendarDetailAction = createActionCreator(
  InstallmentActionTypes.ResetInstallmentCalendarDetail
);

/* REDUCERS */
const initialState: InstallmentState = {
  installmentList: [],
  loadingStatus: LoadingStatus.failure,
  installmentDetail: {},
};

const installmentList = createReducer(initialState.installmentList, {
  [InstallmentActionTypes.GetInstallmentList]: {
    [RequestActionTypes.SUCCESS]: (state: InstallmentDocumentBasic[], payload: any) => {
      return payload;
    },
    [RequestActionTypes.FAILURE]: () => [],
  },
  [InstallmentActionTypes.ResetInstallmentList]: () => [],
});

const loadingStatus = createLoadingStateReducer(
  initialState.loadingStatus,
  {
    [InstallmentActionTypes.GetInstallmentList]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
    [InstallmentActionTypes.GetInstallmentCalendarDetail]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
  },
  {
    [InstallmentActionTypes.SetLoading]: (state: LoadingStatus, status: LoadingStatus) => status,
  }
);

const installmentDetail = createReducer(initialState.installmentDetail, {
  [InstallmentActionTypes.GetInstallmentCalendarDetail]: {
    [RequestActionTypes.SUCCESS]: (
      state: InstallmentDocumentDetailResponse,
      payload: InstallmentDocumentDetailResponse
    ) => payload,
    [RequestActionTypes.FAILURE]: () => [],
    [RequestActionTypes.REQUEST]: () => [],
  },
  [InstallmentActionTypes.ResetInstallmentCalendarDetail]: () => [],
});

export default combineReducers<InstallmentState>({
  installmentList,
  loadingStatus,
  installmentDetail,
});

/* SELECTORS*/
const selectInstallment = (state: AppState) => state.installment;

export const selectInstallmentLoading = (state: AppState): LoadingStatus => selectInstallment(state).loadingStatus;

export const selectInstallmentData = (state: AppState): InstallmentDocumentBasic[] =>
  selectInstallment(state).installmentList;

export const selectInstallmentDetail = (state: AppState): InstallmentDocumentDetailResponse =>
  selectInstallment(state).installmentDetail;

const getSubordinateBoolean = () => {
  const parsedArray = JSON.parse(localStorage.getItem('subordinate') || '{}');

  return parsedArray === true || parsedArray === false ? parsedArray : false;
};

/* SAGAS */
function* getInstallmentCalendarList({ payload }: AppAction<InstallmentCalendarListPayload>) {
  yield put(setInstallmentLoadingAction(LoadingStatus.loading));

  const customer: CustomerInfo = yield select(selectContextCustomerInfo);
  const currentPageId = getCurrentRoute()?.commonPageData.pageId || '';
  const subordinate: boolean = ['2.2.1.1', '2.2.1.4', '2.2.1.6'].includes(currentPageId)
    ? false
    : getSubordinateBoolean();
  const caRefNo: string | undefined = yield select(selectBillingCaRefNo);
  const finalCaRefNo: string | undefined = payload?.caRefNo ?? caRefNo;
  const filter: Array<BillingReqeustFilter> = [
    {
      column: 'cuRefNo',
      compareType: 'EQUAL',
      value: customer?.cuRefNo,
    },
  ];

  if (finalCaRefNo) {
    filter.push({
      column: 'caRefNo',
      compareType: 'EQUAL',
      value: finalCaRefNo,
    });
  }

  const request: ApiClientRequest = {
    apiCall: call(
      api.getInstallmentCalendarList,
      yield getApiUrl(environment.urlConfig.financialInfo),
      JSON.stringify(filter),
      finalCaRefNo || filter.some((item) => item.column === 'caRefNo') ? subordinate || false : undefined
    ),
    suppressError: true,
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getInstallmentListAction.success(resp.data));

    if (payload?.forRedirect) {
      if (resp.data.length === 1) {
        if (currentPageId === '2.2.1.2c') {
          history.replace(
            `${buildRoute(ROUTES.InstalmentCalendarDetail)}?id=${resp.data[0].installmentId}&billingCaRefNo=${
              resp.data[0].caRefNo
            }`
          );
          yield put(
            getInstallmentCalendarDetailAction.request({
              caRefNo: resp.data[0].caRefNo,
              installmentId: resp.data[0].installmentId,
            })
          );
        } else {
          history.replace(
            `${buildRoute(ROUTES.InstalmentCalendarDetail)}?id=${resp.data[0].installmentId}&billingCaRefNo=${
              resp.data[0].caRefNo
            }`
          );
        }
      } else if (payload?.caRefNo) {
        history.replace(`${buildRoute(ROUTES.Instalments)}?caRefNo=${payload?.caRefNo}`);
      } else {
        history.replace(`${buildRoute(ROUTES.Instalments)}?cuRefNo=${customer.cuRefNo}`);
      }
    }

    if (payload?.handleSub) {
      if (resp.data.length > 1) {
        if (payload?.caRefNo) {
          history.replace(`${buildRoute(ROUTES.Instalments)}?caRefNo=${payload?.caRefNo}`);
        } else {
          history.replace(`${buildRoute(ROUTES.Instalments)}?cuRefNo=${customer.cuRefNo}`);
        }
      } else if (currentPageId === '2.2.1.1c') {
        history.replace(
          `${buildRoute(ROUTES.InstalmentCalendarDetail)}?id=${resp.data[0].installmentId}&billingCaRefNo=${
            resp.data[0].caRefNo
          }`
        );
      }
    }
  } else {
    yield put(getInstallmentListAction.failure());

    if (resp.status === 404) {
      yield put(getKahlActions.request());
    }
  }
}

function* getInstallmentCalendarDetail({ payload }: AppAction<InstallmentCalendarDetailPayload>) {
  yield put(setInstallmentLoadingAction(LoadingStatus.loading));

  const request: ApiClientRequest = {
    apiCall: call(
      api.getInstallmentCalendarDetail,
      yield getApiUrl(environment.urlConfig.financialInfo),
      payload.caRefNo,
      payload.installmentId
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getInstallmentCalendarDetailAction.success(resp.data));
  } else {
    yield put(getInstallmentCalendarDetailAction.failure());

    if (resp.status === 404) {
      yield put(
        getKahlOutsideComponentAction({
          ...resp.data,
        })
      );
    }
  }
}

/* EXPORT */
export function* installmentSaga() {
  yield takeLatest(
    createActionType(InstallmentActionTypes.GetInstallmentList, RequestActionTypes.REQUEST),
    getInstallmentCalendarList
  );

  yield takeLatest(
    createActionType(InstallmentActionTypes.GetInstallmentCalendarDetail, RequestActionTypes.REQUEST),
    getInstallmentCalendarDetail
  );
}
